import type { BurnLap, BurnRates, FuelBurn } from './fuelBurn.ts'
import type { RacePlanDoc } from './racePlan.ts'
import type { PitStopDoc } from './timing.ts'

/**
 * Fuel strategy for a race with fixed pit windows: which windows the car has
 * to take fuel in.
 *
 * One question, asked before the race and all through it — given the fuel in
 * the car now, the windows still to come and the fuel stops still owed, which
 * windows must it fuel in? Before the start the fuel is the starting load and
 * every window is ahead; during the race it is what the car has now. One
 * answer to both, so the plan and the pit wall cannot disagree.
 *
 * Windows are clock times and fuel is laps; pace joins them. Everything here
 * assumes green running, which is the safe side: a lap behind a yellow burns
 * less and takes far longer, so a yellow only ever leaves more fuel at a given
 * time than this expects. The live figures pick up what the yellows gave back
 * from the fuel actually used.
 *
 * A fuel stop fills the tank to the top. A driver-change window is a stop
 * anyway, and the time in the pit lane burns next to nothing, so each window
 * moves the fuel's deadline on by its stop. Fuelling at one is the cheap way; where the rules let fuel go in
 * outside the windows, stops the rules demand beyond what the fuel needs can
 * be splashes — late, and under a yellow if one comes.
 */

/** The team's fuel settings for a race, on its plan. */
export interface FuelPlan {
  /** What the tank holds. */
  tankLitres?: number
  /** What it should never be run below. */
  reserveLitres?: number
  /** What it starts the race with, where that is not full. */
  startLitres?: number
  /** Litres per green lap, over the measured rate. */
  greenRate?: number
  /** Litres per lap under a full-course yellow, over the measured rate. */
  fcyRate?: number
  /** A green lap, over the measured pace. */
  lapMs?: number
  /** A stop without fuel, pit in to pit out, over the rules' minimum. */
  stopMs?: number
  /** A stop with fuel, over the rules' minimum. */
  fuelStopMs?: number
}

/** One window still to come, or open now. */
export interface StrategyWindow {
  index: number
  opens: Date
  closes: Date
}

export interface StrategyInput {
  /** Where the fuel is measured from: the race start, or the car's last line crossing. */
  from: Date
  /** When the race ends. The car runs one more lap to the flag. */
  end: Date
  /** Litres in the tank at `from`. */
  litres: number
  tank: number
  reserve: number
  /** Litres per green lap. */
  rate: number
  /** A green lap. */
  lapMs: number
  windows: readonly StrategyWindow[]
  /** Fuel stops the rules still require. */
  stopsOwed: number
  /** Whether fuel may go in outside the windows. */
  anytime: boolean
  /** Whether the car stops in every window anyway, to change driver. */
  stopEveryWindow: boolean
  /** A stop without fuel, pit in to pit out. */
  stopMs: number
  /** A stop with fuel. */
  fuelStopMs: number
}

/** One stop of an option: the window, and when within it the stop has to be. */
export interface PlannedFuelStop {
  window: number
  /** No earlier than this, or the fuel after it will not reach the flag. */
  earliest: Date
  /** No later than this, or the fuel before it runs out. */
  latest: Date
  opens: Date
  closes: Date
}

export interface FuelOption {
  stops: PlannedFuelStop[]
  /** Laps of freedom at the tightest stop: how far either way it can move. */
  marginLaps: number
  /** Stops the rules still want beyond these, made as splashes outside the windows. */
  splashes: number
}

export interface FuelStrategy {
  /** How far a full tank goes. */
  rangeLaps: number
  /** The fuel in the car now, in laps. */
  lapsInCar: number
  /** When the fuel in the car reaches the reserve, at green pace, not stopping. */
  runsOutAt: Date
  /** When it does, as it stands at each window to come: the stops before it buy time. */
  deadlineAt: Map<number, Date>
  /** The fewest window fuel stops that reach the flag, whatever the rules want. Null if none do. */
  fewest: number | null
  /** Ways to the flag, best first: the fewest stops, splashes included, then the widest margin. */
  options: FuelOption[]
}

/** More windows than this and the options are not all tried. */
const maxWindows = 16

export function fuelStrategy(input: StrategyInput): FuelStrategy {
  const { from, end, litres, tank, reserve, rate, lapMs, stopsOwed, anytime } = input
  const lapsOf = (l: number) => l / rate
  const rangeLaps = lapsOf(tank - reserve)
  const range = rangeLaps * lapMs
  const lapsInCar = Math.max(0, lapsOf(litres - reserve))
  const runsOut = from.getTime() + lapsInCar * lapMs
  const needed = end.getTime() + lapMs

  // An open window counts from now; one that has closed is gone.
  const windows = input.windows
    .filter((w) => w.closes.getTime() > from.getTime())
    .map((w) => ({ ...w, o: Math.max(w.opens.getTime(), from.getTime()), c: w.closes.getTime() }))
    .slice(0, maxWindows)
  const n = windows.length
  // Time in the pit lane burns next to nothing, so every window's stop moves
  // the fuel's deadline on by its length: those strictly between a and b.
  const paused = (a: number, b: number) => (input.stopEveryWindow ? Math.max(0, b - a - 1) * input.stopMs : 0)
  const fuelStop = input.fuelStopMs

  const options: FuelOption[] = []
  for (let mask = 0; mask < 1 << n; mask++) {
    const at: number[] = []
    for (let i = 0; i < n; i++) if (mask & (1 << i)) at.push(i)
    const k = at.length
    if (!anytime && k < stopsOwed) continue

    // As late as the fuel before allows, and as early as the fuel after needs.
    const latest: number[] = []
    let reach = runsOut + paused(-1, at[0] ?? n)
    for (let j = 0; j < k; j++) {
      const w = windows[at[j]!]!
      const l = Math.min(w.c, reach)
      latest.push(l)
      reach = l + fuelStop + range + paused(at[j]!, at[j + 1] ?? n)
    }
    if (reach < needed) continue
    const earliest: number[] = new Array<number>(k)
    let need = needed
    for (let j = k - 1; j >= 0; j--) {
      const w = windows[at[j]!]!
      earliest[j] = Math.max(w.o, need - range - fuelStop - paused(at[j]!, at[j + 1] ?? n))
      need = earliest[j]!
    }
    if (at.some((_, j) => earliest[j]! > latest[j]!)) continue

    options.push({
      stops: at.map((i, j) => ({
        window: windows[i]!.index,
        earliest: new Date(earliest[j]!),
        latest: new Date(latest[j]!),
        opens: windows[i]!.opens,
        closes: windows[i]!.closes,
      })),
      marginLaps:
        k === 0 ? (reach - needed) / lapMs : Math.min(...at.map((_, j) => (latest[j]! - earliest[j]!) / lapMs)),
      splashes: Math.max(0, stopsOwed - k),
    })
  }

  // The fewest stops first — a splash is a stop like any other, and dearer
  // than fuel at a stop being made anyway — then the widest margin; then the
  // earliest windows, which leave the later ones free for what the race brings.
  options.sort(
    (a, b) =>
      a.stops.length + a.splashes - (b.stops.length + b.splashes) ||
      b.marginLaps - a.marginLaps ||
      a.stops.length - b.stops.length ||
      a.stops.map((s) => s.window).join(',').localeCompare(b.stops.map((s) => s.window).join(',')),
  )
  return {
    rangeLaps,
    lapsInCar,
    runsOutAt: new Date(runsOut),
    // Running on without fuel, the deadline as it stands at each window:
    // pushed on by the stops in the windows before it.
    deadlineAt: new Map(windows.map((w, i) => [w.index, new Date(runsOut + paused(-1, i))])),
    fewest: options.length > 0 ? Math.min(...options.map((o) => o.stops.length)) : null,
    options,
  }
}

/**
 * What the next window means for fuel, for the pit wall:
 *
 *   must      no way to the flag without fuel in it
 *   early     must, and the fuel will not last much past its opening
 *   either    can be skipped, but fuel has to go in at it or the next
 *   skip      not needed here at all
 *   short     the fuel will not reach it
 *   finish    no window needs fuel: the car makes the flag
 *   impossible no way to the flag on these numbers
 */
export type WindowCall = 'must' | 'early' | 'either' | 'skip' | 'short' | 'finish' | 'impossible'

export interface WindowAdvice {
  call: WindowCall
  /** The window the call is about: open now, or next to open. */
  window: StrategyWindow | null
  /**
   * Latest the stop can be, where it must be in this window. Short of the
   * window where fuel may go in at any time, the latest for a stop outside it.
   */
  by: Date | null
  /** Laps of fuel to spare when the window opens and closes, negative when short. */
  atOpen: number | null
  atClose: number | null
  /** The same for the window after. */
  following: { window: StrategyWindow; atOpen: number; atClose: number } | null
  /** The windows the best option fuels in. */
  plan: number[]
  splashes: number
}

/** How near the opening "must" becomes "as soon as it opens". */
const earlyLaps = 2

export function adviseWindow(strategy: FuelStrategy, input: StrategyInput): WindowAdvice {
  const t = input.from.getTime()
  const ahead = input.windows.filter((w) => w.closes.getTime() > t)
  const window = ahead[0] ?? null
  const after = ahead[1] ?? null
  const spare = (w: StrategyWindow, at: Date) =>
    ((strategy.deadlineAt.get(w.index) ?? strategy.runsOutAt).getTime() - Math.max(at.getTime(), t)) / input.lapMs
  const best = strategy.options[0]
  const base = {
    window,
    atOpen: window ? spare(window, window.opens) : null,
    atClose: window ? spare(window, window.closes) : null,
    following: after
      ? { window: after, atOpen: spare(after, after.opens), atClose: spare(after, after.closes) }
      : null,
    plan: best?.stops.map((s) => s.window) ?? [],
    splashes: best?.splashes ?? 0,
  }

  if (!best) {
    const short = window != null && spare(window, window.opens) < 0
    // Where fuel may go in at any time, short of the window is a stop for fuel
    // before the tank runs down, and the windows after it planned from a full
    // tank then.
    if (short && input.anytime) {
      const by = strategy.runsOutAt
      const then = fuelStrategy({
        ...input,
        from: by,
        litres: input.tank,
        stopsOwed: Math.max(0, input.stopsOwed - 1),
      }).options[0]
      return { call: 'short', by, ...base, plan: then?.stops.map((s) => s.window) ?? [], splashes: then?.splashes ?? 0 }
    }
    return { call: short ? 'short' : 'impossible', by: null, ...base }
  }
  if (best.stops.length === 0) return { call: 'finish', by: null, ...base }
  if (!window) return { call: 'finish', by: null, ...base }

  const without = strategy.options.find((o) => !o.stops.some((s) => s.window === window.index))
  if (without) {
    const needsNext = after && !strategy.options.some((o) => !o.stops.some((s) => s.window === window.index || s.window === after.index))
    return { call: needsNext ? 'either' : 'skip', by: null, ...base }
  }
  const stop = best.stops.find((s) => s.window === window.index)!
  // The latest it can be, over every option that fuels here.
  const by = Math.max(
    ...strategy.options.flatMap((o) => o.stops.filter((s) => s.window === window.index).map((s) => s.latest.getTime())),
  )
  const opens = Math.max(stop.opens.getTime(), t)
  return {
    call: (by - opens) / input.lapMs < earlyLaps ? 'early' : 'must',
    by: new Date(by),
    ...base,
  }
}

/**
 * Fuel stops made since a place in the event's count of laps: the race
 * start, for what the rules still want. Fuel that went in on the grid is not
 * a stop.
 */
export function fuelStopsSince(burn: FuelBurn, position: number): number {
  const refuels = [...burn.stretches.flatMap((s) => s.refuels), ...burn.open.refuels]
  return refuels.filter((r) => r.position > position).length
}

/** A stop the fills have not answered yet: whether fuel went in is not known. */
export interface UnfilledStop {
  stopNumber: number
  /** The lap the car came in on. */
  lap: number
  pitIn: Date
  /** Null while the car is still in the pits. */
  out: Date | null
}

/**
 * The car's last stop, where no fill has been reported at or after it — or
 * the stop it is making now.
 *
 * The fuel station's figure comes in during the stop or after it — a minute
 * or two where the station books it there and then, longer where somebody
 * types it into a sheet — and until it does the stop could have been for fuel
 * or only for a driver change: nothing in the fills can say which. Only the
 * last stop: one before it that still has no fill took none.
 *
 * A fill placed a lap short of the stop still answers it; stops are far
 * enough apart that it cannot be the one before's.
 */
export function unfilledStop(
  burn: FuelBurn,
  raceStart: number,
  stops: readonly Pick<PitStopDoc, 'stopNumber' | 'lap' | 'completedAt' | 'stopLengthMs'>[],
  /** The car is in the pits now: came in after completing `lap`, at `at`. */
  inPits?: { lap: number; at: Date } | null,
): UnfilledStop | null {
  const done = stops
    .filter((s) => s.completedAt)
    .sort((a, b) => a.completedAt!.toMillis() - b.completedAt!.toMillis())
  const last = done.at(-1)
  const stop: UnfilledStop | null = inPits
    ? { stopNumber: Math.max(0, ...done.map((s) => s.stopNumber)) + 1, lap: inPits.lap, pitIn: inPits.at, out: null }
    : last
      ? {
          stopNumber: last.stopNumber,
          lap: last.lap,
          pitIn: new Date(last.completedAt!.toMillis() - (last.stopLengthMs ?? 0)),
          out: last.completedAt!.toDate(),
        }
      : null
  if (!stop) return null
  const refuels = [...burn.stretches.flatMap((s) => s.refuels), ...burn.open.refuels]
  return refuels.some((r) => r.position >= raceStart + stop.lap - 1) ? null : stop
}

/**
 * Whether the plan, as it stood when the car came in, had it take fuel at
 * this stop — what to assume of a stop until somebody says or the fill comes.
 *
 * In a window, only if it had to: every way to the flag fuels in it, or there
 * was none. A window the plan merely preferred is as often skipped — at
 * Portimão the car went through one it could have fuelled in and took fuel
 * outside the windows instead. Outside the windows, where the drivers change
 * only in them, the stop can only have been for fuel.
 */
export function planFuelsAt(input: StrategyInput, at: Date): boolean {
  const t = at.getTime()
  const window = input.windows.find((w) => w.opens.getTime() <= t && t < w.closes.getTime())
  if (!window) return input.stopEveryWindow
  const { options } = fuelStrategy({ ...input, from: at })
  return options.every((o) => o.stops.some((s) => s.window === window.index))
}

/** The rates the strategy runs on: the plan's where it sets them, else the measured. */
export function strategyRates(plan: FuelPlan | undefined, measured: BurnRates | null | undefined) {
  const green = plan?.greenRate ?? measured?.green ?? null
  return {
    green,
    // Without a yellow rate, the green one: more fuel used, never less.
    fcy: plan?.fcyRate ?? measured?.fcy ?? green,
    greenFrom: plan?.greenRate != null ? ('plan' as const) : measured?.green != null ? ('measured' as const) : null,
  }
}

/**
 * The fuel in the car now.
 *
 * From the last fill to the top, less what the laps since used — green and
 * yellow at their own rates — plus any short fills since. Where the plan says
 * the race started with a set load and no full fill has happened since the
 * start, from that load instead.
 */
export function fuelInCar(
  burn: FuelBurn,
  options: {
    tank: number
    green: number
    fcy: number
    /** The race session, and what the plan says it started with. */
    race?: { session: string; startLitres?: number }
  },
): { litres: number; laps: number; fcyLaps: number; fromStart: boolean } {
  const used = (s: { laps: number; fcyLaps: number }) => (s.laps - s.fcyLaps) * options.green + s.fcyLaps * options.fcy
  const raceStart = options.race ? burn.sessionStarts[options.race.session] : undefined
  if (options.race?.startLitres != null && raceStart != null && raceStart >= burn.open.from) {
    const s = burn.span(raceStart, burn.total)
    const added = burn.open.refuels.filter((r) => r.position > raceStart).reduce((n, r) => n + r.litres, 0)
    return { litres: Math.min(options.tank, options.race.startLitres - used(s) + added), ...s, fromStart: true }
  }
  const s = burn.span(burn.open.from, burn.total)
  return { litres: Math.min(options.tank, options.tank - used(s) + burn.open.shortLitres), ...s, fromStart: false }
}

/**
 * A green lap: the median of the car's clean laps, from the race where it has
 * run enough of them, else from the whole event.
 */
export function greenPace(laps: readonly BurnLap[], session?: string): number | null {
  const clean = laps.filter((l) => l.fcy === 0 && !l.pit && l.lapMs != null && l.lapMs > 0)
  const race = session ? clean.filter((l) => l.session === session) : []
  const use = race.length >= 5 ? race : clean
  if (use.length === 0) return null
  const ms = use.map((l) => l.lapMs!).sort((a, b) => a - b)
  return ms[Math.floor(ms.length / 2)]!
}

/** Where a number the strategy runs on came from, for saying so beside it. */
export type SettingFrom = 'plan' | 'measured' | 'rules' | 'default'

export interface StrategySetup {
  /** Null until everything it needs is known; `missing` says what is not. */
  input: StrategyInput | null
  missing: string[]
  rate: { value: number | null; from: SettingFrom | null }
  fcyRate: { value: number | null; from: SettingFrom | null }
  pace: { value: number | null; from: SettingFrom | null }
  stopMs: { value: number; from: SettingFrom }
  fuelStopMs: { value: number; from: SettingFrom }
  stopsRequired: number
}

/** A stop when nothing says otherwise: a driver change, and one with fuel. */
const defaultStopMs = 60_000
const defaultFuelStopMs = 120_000

/**
 * The strategy's inputs from a race plan and what has been measured.
 *
 * The plan's own figures win; then the rules' minimum stop times; then the
 * measured burn and pace. From the race start with the plan's starting load
 * where `now` is not given; from the car's fuel and position when it is.
 */
export function strategySetup(
  plan: Pick<RacePlanDoc, 'startsAt' | 'endsAt' | 'regime' | 'fuel'>,
  options: {
    measured: BurnRates | null | undefined
    pace: number | null
    windows: readonly StrategyWindow[]
    /** Live: where the car is now, its fuel, and the fuel stops made so far. */
    now?: { at: Date; litres: number; stopsMade: number }
  },
): StrategySetup {
  const fuel = plan.fuel ?? {}
  const { regime } = plan
  const rates = strategyRates(fuel, options.measured)
  const rate = { value: rates.green, from: rates.greenFrom }
  const fcyRate = {
    value: rates.fcy,
    from: fuel.fcyRate != null ? ('plan' as const) : options.measured?.fcy != null ? ('measured' as const) : rate.from,
  }
  const pace = {
    value: fuel.lapMs ?? options.pace,
    from: fuel.lapMs != null ? ('plan' as const) : options.pace != null ? ('measured' as const) : null,
  }
  const dc = regime.components.driverChange.minDurationMs
  const stopMs =
    fuel.stopMs != null
      ? { value: fuel.stopMs, from: 'plan' as const }
      : dc != null
        ? { value: dc, from: 'rules' as const }
        : { value: defaultStopMs, from: 'default' as const }
  const rf = regime.components.refuel.minDurationMs
  const fuelStopMs =
    fuel.fuelStopMs != null
      ? { value: fuel.fuelStopMs, from: 'plan' as const }
      : rf != null
        ? { value: Math.max(rf, stopMs.value), from: 'rules' as const }
        : { value: Math.max(defaultFuelStopMs, stopMs.value), from: 'default' as const }
  const stopsRequired = regime.components.refuel.minCount ?? 0

  const missing: string[] = []
  if (!fuel.tankLitres) missing.push('the tank size')
  if (rate.value == null) missing.push('a burn rate')
  if (pace.value == null) missing.push('a lap time')
  if (plan.startsAt == null || plan.endsAt == null) missing.push('the race times')
  if (options.windows.length === 0) missing.push('the pit windows')
  const base = { missing, rate, fcyRate, pace, stopMs, fuelStopMs, stopsRequired }
  if (missing.length > 0) return { input: null, ...base }

  const tank = fuel.tankLitres!
  return {
    input: {
      from: options.now?.at ?? new Date(plan.startsAt!),
      end: new Date(plan.endsAt!),
      litres: options.now?.litres ?? Math.min(tank, fuel.startLitres ?? tank),
      tank,
      reserve: fuel.reserveLitres ?? 0,
      rate: rate.value!,
      lapMs: pace.value!,
      windows: options.windows,
      stopsOwed: Math.max(0, stopsRequired - (options.now?.stopsMade ?? 0)),
      anytime: regime.components.refuel.allowed === 'anytime',
      stopEveryWindow: regime.components.driverChange.allowed === 'windowOnly',
      stopMs: stopMs.value,
      fuelStopMs: fuelStopMs.value,
    },
    ...base,
  }
}

/** The call in words, and how loud to say it. */
export function adviceLine(
  advice: WindowAdvice,
  clock: (d: Date) => string,
): { line: string; tone: 'green' | 'yellow' | 'critical' } {
  const w = advice.window
  const name = w ? `W${w.index}` : 'the window'
  const next = advice.following ? `W${advice.following.window.index}` : 'the next window'
  switch (advice.call) {
    case 'must':
      return { line: `Fuel in ${name}, by ${clock(advice.by!)}`, tone: 'yellow' }
    case 'early':
      return { line: `Fuel in ${name} as soon as it opens — by ${clock(advice.by!)}`, tone: 'critical' }
    case 'either':
      return { line: `Can skip ${name}, then ${next} must take fuel`, tone: 'green' }
    case 'skip':
      return { line: `No fuel needed in ${name}`, tone: 'green' }
    case 'short':
      return advice.by
        ? { line: `Fuel by ${clock(advice.by)}: ${name} is out of reach`, tone: 'critical' }
        : { line: `Short of ${name} by ${Math.ceil(-(advice.atOpen ?? 0))} laps`, tone: 'critical' }
    case 'finish':
      return { line: 'Enough fuel to the flag', tone: 'green' }
    case 'impossible':
      return { line: 'No way to the flag on these numbers', tone: 'critical' }
  }
}
